/**
 * Sharing the screen with the docked chat panel — MYAC-1126 §1.
 *
 * A dialog and the assistant could not both be used at once: the panel is
 * DOCKED, pushing page content aside rather than floating over it, but a modal
 * dialog's mask covers the whole viewport — panel included. Pressing "Discuss
 * this action" opened a chat the user could see and could not touch.
 *
 * The rules live here rather than inline in one dialog because they are not
 * that dialog's own — and since MYAC-1541 they are not any dialog's to apply
 * either. They were the shared shell's (`AppDialog`), which four dialogs use;
 * the panel is lifted over EVERY mask, so the eighty-odd plain `<Dialog>`s kept
 * their right side under it, "Manage referentials" first among them. The panel
 * now publishes what it takes on the document root (`syncBesideChat`) and one
 * rule in `assets/main.css` makes every page mask give it up: the same shape
 * as the lift itself, a condition the dialogs cannot forget to satisfy.
 */
import type { ChatPanelRegime } from '@/stores/chatPanel'

/**
 * Whether a dialog should step aside for the panel.
 *
 * Docked OR floating — MYAC-1287. This used to be docked only, on the grounds
 * that a floating panel overlays the content and stepping aside would put the
 * dialog under it. That held for the first mechanism (`position="left"`, no
 * mask); it does not for the padded mask, which moves the dialog LEFT of a
 * panel that is lifted over the mask in every regime. What the docked-only rule
 * did instead was leave the dialog under the floating panel, and full screen
 * made that impossible to miss: its Exit and ✕ buttons sit at the right edge,
 * exactly where the panel floats (QA run 2026-09-16, 1280px, Linux).
 *
 * The regime is not a stable fact to lean on either. The panel docks only with
 * `MIN_CONTENT_WIDTH` left beside it, and opening a modal used to pad the body
 * by the scrollbar's width (until MYAC-1120) — so on a 1280px window the SAME
 * panel docked when opened before the dialog and floated when opened after it.
 * Stepping aside for both makes the order irrelevant, whatever else ever moves
 * that line.
 *
 * Not in `takeover`: the panel takes the whole viewport there, so there is no
 * side to step to. Open-but-hidden still says no — a dialog stepping aside for
 * a panel that is not on screen yields half the row to nothing.
 *
 * Takes the panel's VISIBILITY, not its `isOpen` flag: on a dedicated chat page
 * the dock is held off the screen with `isOpen` untouched (MYAC-1156), and a
 * dialog stepping aside for a panel that is not there yields half the row to
 * nothing.
 */
export function isBesideChat(isVisible: boolean, regime: ChatPanelRegime): boolean {
  return isVisible && (regime === 'docked' || regime === 'overlay')
}

/**
 * Where the dialog sits when it shares the row — MYAC-1126 follow-up.
 *
 * Sy: "in bigger screen, the dialog go to far left which looks weird … when
 * there is a chat, the screen becomes 2 separate parts, keep the dialog in the
 * center of the other half."
 *
 * It used to take `position="left"`, which pins it to the viewport edge. On a
 * wide screen that leaves a lake of empty page between the dialog and the panel
 * it is supposed to be talking to.
 *
 * So the dialog stays CENTRED and the mask is narrowed instead. PrimeVue's mask
 * is `position: fixed; width: 100%` with `justify-content: center`
 * (`dialog/style/index.mjs:6`), so padding its right edge by the panel's width
 * shrinks the box it centres within to exactly the content half — the dialog's
 * centre lands at `(100vw - panelWidth) / 2` with no transform and no
 * measuring. `pt` binds after the built-in style, so this adds rather than
 * fights.
 *
 * Relies on `box-sizing: border-box`, which Tailwind's preflight sets globally:
 * the padding has to come OUT of the 100% width, not add to it.
 *
 * **Exactly the panel, in both regimes — no scrollbar term.** MYAC-1287 added
 * `var(--p-scrollbar-width)` for the docked panel, because PrimeVue's scroll
 * lock padded the body by the scrollbar's width and pushed the in-flow panel
 * that far left. MYAC-1120 cancels that padding (`assets/main.css`): the page's
 * scrollbar gutter already reserves the space. The docked panel now stays put
 * when a modal opens, like the floating `Drawer` always did, so the term would
 * open a 15px gap between the dialog and either panel.
 */
export function besideMaskStyle(panelWidth: number): { paddingRight: string } {
  return { paddingRight: `${panelWidth}px` }
}

/** Present on the document root while a page dialog has to step aside. */
export const BESIDE_CHAT_ATTR = 'data-chat-beside'
/** What the panel takes off the right of every page mask, as a CSS length. */
export const BESIDE_INSET_VAR = '--chat-beside-inset'

/**
 * Tell the stylesheet what the panel takes, or that it takes nothing.
 *
 * On the root rather than on each mask: PrimeVue appends a mask as the dialog
 * opens, and a value written onto it after the fact arrives a frame late —
 * the dialog would open centred on the viewport and then jump. A rule keyed on
 * the root already matches the mask the instant it exists.
 */
export function syncBesideChat(root: HTMLElement, inset: string | null): void {
  if (inset === null) {
    root.removeAttribute(BESIDE_CHAT_ATTR)
    root.style.removeProperty(BESIDE_INSET_VAR)
    return
  }
  root.setAttribute(BESIDE_CHAT_ATTR, '')
  root.style.setProperty(BESIDE_INSET_VAR, inset)
}
